import React, { ReactNode, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './SearchInput.styles';
import { Input } from '@/components/UI/Input/Input';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { InputAppearance } from '@/components/UI/Input/Input.types';

interface Props {
  onSearch: (value: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  hasBorderBottom?: boolean;
  hasPadding?: boolean;
  appearance?: InputAppearance;
  customPaddingTop?: number;
  customPaddingBottom?: number;
  leftIcon?: ReactNode;
}

export interface SearchInputRef {
  clear: () => void;
}

export const SearchInput = React.forwardRef<SearchInputRef, Props>(
  (
    {
      onSearch,
      placeholder,
      isLoading,
      hasBorderBottom = true,
      hasPadding = true,
      appearance = 'filledWithIcon',
      customPaddingTop,
      customPaddingBottom,
      leftIcon,
    },
    CategorySearchRef
  ) => {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState('');

    function clear() {
      setSearchValue('');
      onSearch('');
    }

    function onChange(value: string) {
      setSearchValue(value);
      onSearch(value);
    }

    useImperativeHandle(CategorySearchRef, () => ({ clear }), []);

    return (
      <S.Container $hasPadding={hasPadding} $hasBorderBottom={hasBorderBottom}>
        {isLoading ? (
          <SkeletonLoader
            dataTestId="search-skeleton-loader"
            borderRadius={7}
            height="40px"
          />
        ) : (
          <Input
            type="search"
            value={searchValue}
            onChange={value => onChange(value as string)}
            appearance={appearance}
            customPaddingTop={customPaddingTop}
            customPaddingBottom={customPaddingBottom}
            placeholder={placeholder ?? t('common.search')}
            leftIcon={leftIcon}
            clear={clear}
          />
        )}
      </S.Container>
    );
  }
);

SearchInput.displayName = 'SearchInput';
